/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { useState } from 'react';
import { observer } from 'mobx-react';
import { Theme } from '@material-ui/core/styles';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Box, CircularProgress, Grid, Typography } from '@material-ui/core';
import { Button, NumberFormat, TextField } from '@roc/ui';
import { isNotBlank } from '@roc/client-portal-shared/utils';
import { useStore } from '../../hooks';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fieldGrid: {
      padding: theme.spacing(2, 0),
    },
  })
);

interface Props {
  handleBackStep: () => void;
  handleNextStep: () => void;
}

interface FieldErrors {
  name?: string;
  email?: string;
  phoneNumber?: string;
}

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isValidPhone = (value: string) => value.replace(/\D/g, '').length === 10;

const PointOfContactStep = observer((props: Props) => {
  const classes = useStyles();
  const { valuationPaymentStore } = useStore();
  const { pointOfContact, savingPointOfContact } = valuationPaymentStore;
  const [errors, setErrors] = useState<FieldErrors>({});

  const validate = () => {
    const nextErrors: FieldErrors = {};
    const name = pointOfContact.name?.trim();
    const email = pointOfContact.email?.trim();
    const phoneNumber = pointOfContact.phoneNumber?.trim();

    if (!name) {
      nextErrors.name = 'Name is required';
    }

    if (!email) {
      nextErrors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      nextErrors.email = 'Email format is invalid';
    }

    if (!phoneNumber) {
      nextErrors.phoneNumber = 'Phone number is required';
    } else if (!isValidPhone(phoneNumber)) {
      nextErrors.phoneNumber = 'Phone number is invalid';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleContinue = async () => {
    if (!validate()) {
      return;
    }

    const saved = await valuationPaymentStore.savePointOfContact();
    if (saved) {
      props.handleNextStep();
    }
  };

  const handleFieldChange = (
    field: 'name' | 'email' | 'phoneNumber' | 'propertyAccessInstructions',
    value: string
  ) => {
    valuationPaymentStore.setPointOfContactField(field, value);
    setErrors({
      ...errors,
      [field]: '',
    });
  };

  return (
    <Box style={{ maxWidth: 840, margin: 'auto' }}>
      <Box mb={2}>
        <Typography variant="h4">
          Point of Contact
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} className={classes.fieldGrid}>
          <TextField
            testId="pointOfContactName"
            variant="outlined"
            standaloneLabel
            label="Point of Contact Name"
            value={pointOfContact.name}
            onChange={e => handleFieldChange('name', e.target.value)}
            disabled={savingPointOfContact}
            fullWidth
            helperText={errors.name}
            error={isNotBlank(errors.name || '')}
          />
        </Grid>
        <Grid item xs={12} md={6} className={classes.fieldGrid}>
          <TextField
            testId="pointOfContactEmail"
            variant="outlined"
            standaloneLabel
            label="Point of Contact Email"
            value={pointOfContact.email}
            onChange={e => handleFieldChange('email', e.target.value)}
            disabled={savingPointOfContact}
            fullWidth
            helperText={errors.email}
            error={isNotBlank(errors.email || '')}
          />
        </Grid>
        <Grid item xs={12} md={6} className={classes.fieldGrid}>
          <NumberFormat
            testId="pointOfContactPhoneNumber"
            variant="outlined"
            customInput={TextField}
            format="###-###-####"
            mask="_"
            standaloneLabel
            label="Point of Contact Phone Number"
            value={pointOfContact.phoneNumber}
            onChange={e => handleFieldChange('phoneNumber', e.target.value)}
            disabled={savingPointOfContact}
            fullWidth
            helperText={errors.phoneNumber}
            error={isNotBlank(errors.phoneNumber || '')}
          />
        </Grid>
        <Grid item xs={12} className={classes.fieldGrid}>
          <TextField
            testId="propertyAccessInstructions"
            variant="outlined"
            standaloneLabel
            multiline
            minRows={3}
            label="Property Access Instructions"
            value={pointOfContact.propertyAccessInstructions}
            onChange={e => handleFieldChange('propertyAccessInstructions', e.target.value)}
            disabled={savingPointOfContact}
            fullWidth
          />
        </Grid>
      </Grid>

      <Box mt={8}>
        <Grid container justifyContent="center" spacing={2}>
          <Grid item xs={6} md={3}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={props.handleBackStep}
              disabled={savingPointOfContact}
              testId="back"
            >
              Back
            </Button>
          </Grid>
          <Grid item xs={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleContinue}
              disabled={savingPointOfContact}
              testId="continuePointOfContact"
            >
              {savingPointOfContact ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
});

export default PointOfContactStep;
